import { JsonDataValue } from '../utils';

export enum StepType {
  Welcome = 'Welcome',
  SingleSelectAnswer = 'SingleSelectAnswer',
  UploadImages = 'UploadImages',
  MultiSelectAnswer = 'MultiSelectAnswer',
  Finish = 'Finish',
  SelectFinish = 'SelectFinish',
  Confirmation = 'Confirmation',
  Legal = 'Legal',
  Accordion = 'Accordion',
  MailDoB = 'MailDoB',
  PillSelect = 'PillSelect',
  Landing = 'Landing',
  Intro = 'Intro',
  MailDoBFinish = 'MailDoBFinish',
  Interstitial = 'Interstitial',
}

/**
 * A modal opened from a link inside a rich-text block. `id` is what an
 * `<a href="#id">` in the surrounding HTML refers to.
 */
export type RichTextModal = {
  id: string;
  title: string;
  content: string;
};

/**
 * The small confirmation row above the Landing headline, e.g.
 * "Schnellcheck abgeschlossen · Du kommst in Frage".
 */
export type LandingStatus = {
  label: string;
  /** Muted continuation after the label. */
  note?: string;
};

/**
 * The product card on Landing. Authored in the template as the default and
 * overridable at runtime by the host - see `SurveyOptions.product` in
 * packages/survey-form. Display-only: nothing here is submitted or read back,
 * so it never has to agree with what Shopify later charges for.
 */
export type LandingProduct = {
  imageURL: string;
  /** Small uppercase label above the name, e.g. "Dein Serum". */
  eyebrow?: string;
  name: string;
  /** Pill under the name, e.g. "Verschreibungspflichtig". */
  tag?: string;
};

/** One face in the Landing advisory-board avatar stack. */
export type LandingAdvisor = {
  imageURL: string;
  alt?: string;
};

/**
 * Icon a trust chip may use, resolved against survey-form's own icon set. A
 * badge carrying one renders as a chip; a badge carrying an `imageURL` instead
 * renders as the older image card.
 */
export type BadgeIcon = 'clock' | 'lock' | 'shield' | 'check';

export type LandingBadge = {
  icon?: BadgeIcon | string;
  imageURL?: string;
  label: string;
};

/**
 * `searchID` of the step asking for the biological gender. Tagging the step
 * instead of relying on its position is what lets the survey and the backend
 * locate that answer even after the steps have been reordered.
 */
export const GENDER_SEARCH_ID = 'gender';

/**
 * Every `searchID` the pipeline actually reads. The step editor offers exactly
 * these, because a tag nothing consumes is indistinguishable from a typo.
 * Adding a semantic field means adding its tag here.
 */
export const KNOWN_SEARCH_IDS: readonly string[] = [GENDER_SEARCH_ID];

export type StepDataBase = {
  id: number;
  // type: string
  title: string;
  question: string;
  next: string;
  /**
   * Optional semantic tag. Copied into the collected answer, so a specific
   * field can be found by meaning instead of by position.
   */
  searchID?: string;
};

export type StepDataWelcome = StepDataBase & {
  type: StepType.Welcome | string;
  followerId: number;
};

export type StepDataIntro = StepDataBase & {
  type: StepType.Intro | string;
  exit: string;
  exitLink: string;
  followerId: number;
};

/**
 * The rich start slide. Every block below the headline is optional, so a step
 * with only `question`/`next` still renders as a plain welcome screen.
 */
export type StepDataLanding = StepDataBase & {
  type: StepType.Landing | string;
  /** Confirmation row rendered above the headline. */
  status?: LandingStatus;
  extra?: string;
  /** Default product card; the host may override it at runtime. */
  product?: LandingProduct;
  /** Overlapping avatar stack, rendered next to `advisorsText`. */
  advisors?: LandingAdvisor[];
  /** Sentence beside the avatar stack (HTML). */
  advisorsText?: string;
  exit?: string;
  exitLink?: string;
  consent?: string;
  footnote?: string;
  badges?: LandingBadge[];
  modals?: RichTextModal[];
  followerId: number;
};

export type StepDataText = StepDataBase & {
  type: StepType.SingleSelectAnswer | string;
  tip?: string;
  followerId: number;
  inputType: 'text' | 'date';
  inputRegex?: string;
};

export type StepDataFinish = StepDataBase & {
  type: StepType.Finish | string;
  extra?: string;
  succeed: boolean;
};

export type StepDataSelectFinish = StepDataBase & {
  type: StepType.SelectFinish | string;
  extra?: string;
  exit: string;
  body: string;
};

export type AccordionOption = {
  title: string;
  content: string;
};

export type SingleTextOption = {
  type: string; //'SingleSelectOptionText',
  option: string;
  followerId: number;
};

export type SingleImageOption = SingleTextOption & {
  imageLabel: string;
  imageURL: string;
};

export type SingleOption = SingleTextOption | SingleImageOption;

export type StepDataSingle = StepDataBase & {
  type: StepType.SingleSelectAnswer | string;
  extra?: string;
  tip?: string;
  options: SingleOption[];
};

export type StepDataConfirmation = StepDataBase & {
  type: StepType.Confirmation | string;
  followerId: number;
  extraPrompt?: string;
  extra?: string;
};

/**
 * Sentiment of a multi-select option. Drives which follower the step navigates
 * to: any selected `Negative` option wins over an all-`Positive` selection.
 */
export enum OptionSentiment {
  Positive = 'positive',
  Negative = 'negative',
}

export type MultiTextOption = {
  type: string; //'SingleSelectOptionText',
  option: string;
  sentiment: OptionSentiment | string;
  /**
   * Marks a logically exclusive answer such as "Keine der oben genannten":
   * picking it clears every other selection, and picking anything else clears
   * it. Defaults to false - a plain option coexists with all others.
   */
  exclusive?: boolean;
  /**
   * Nests this option under the option whose `option` label matches. One level
   * only. A parent is never itself selectable and never appears in the answer -
   * it acts as a disclosure for its children. Absent on every legacy option, so
   * a step without any `parentOption` renders as the flat list it always was.
   */
  parentOption?: string;
};

export type MultiImageOption = MultiTextOption & {
  imageLabel: string;
  imageURL: string;
};

export type MultiOption = MultiTextOption | MultiImageOption;

export type StepDataMulti = StepDataBase & {
  type: StepType.MultiSelectAnswer | string;
  options: MultiOption[];
  /** Followed when at least one selected option is negative. */
  negativeFollowerId: number;
  /** Followed when every selected option is positive. */
  positiveFollowerId: number;
};

export type StepDataLegal = StepDataBase & {
  type: StepType.Legal | string;
  followerId: number;
  accept: string | string[];
  extra?: string;
};

export type StepDataAccordion = StepDataLegal & {
  type: StepType.Accordion | string;
  sections: AccordionOption[];
};

export type StepDataMailDoB = StepDataBase & {
  type: StepType.MailDoB | string;
  extra?: string;
  followerId: number;
};

/**
 * The MailDoB screen as a closing step: the same e-mail + date-of-birth fields
 * and single CTA, plus a consent checkbox and Landing-style modals. Pressing
 * the CTA submits the survey as succeeded, so there is no follower.
 */
export type StepDataMailDoBFinish = Omit<StepDataMailDoB, 'type' | 'followerId'> & {
  type: StepType.MailDoBFinish | string;
  /** Checkbox label (HTML). Omit it and only the two fields gate the CTA. */
  consent?: string;
  footnote?: string;
  modals?: RichTextModal[];
};

/**
 * A slide that shows something and moves on - motivation, reassurance, a
 * result. It asks nothing, so App strips its answer from the payload before
 * submission: it never reaches the stored survey or the record a doctor reads.
 *   eyebrow  - small uppercase label above the headline
 *   question - the headline (from StepDataBase)
 *   extra    - lead paragraph (HTML)
 *   body     - freeform middle section (HTML); no layout is imposed on it, so a
 *              slide can be anything. Links to a `modals` entry work as they do
 *              on Landing.
 *   next     - CTA label
 */
export type StepDataInterstitial = StepDataBase & {
  type: StepType.Interstitial | string;
  followerId: number;
  eyebrow?: string;
  extra?: string;
  body?: string;
  modals?: RichTextModal[];
};

export type StepDataPillSelect = StepDataSingle & {
  type: StepType.PillSelect | string;
  items: string[];
};

export type StepData =
  | StepDataWelcome
  | StepDataText
  | StepDataSingle
  | StepDataMulti
  | StepDataConfirmation
  | StepDataFinish
  | StepDataSelectFinish
  | StepDataMailDoBFinish
  | StepDataLegal
  | StepDataAccordion
  | StepDataMailDoB
  | StepDataPillSelect
  | StepDataLanding
  | StepDataIntro
  | StepDataInterstitial
  | JsonDataValue;

export type SurveyTemplate = {
  templateId: string;
  name: string;
  description: string;
  version: string;
  draft: boolean;
  tags: string[];
  steps: StepData[];
};

export type TemplateAddInput = {
  name: string;
  description: string;
};

export type TemplateUpdateInput = {
  templateId: string;
  name?: string;
  description?: string;
  draft?: boolean;
  tags?: string[];
  steps?: StepData[];
};
